import { useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { TimerDisplay } from "./timer/TimerDisplay";
import { TimerControls } from "./timer/TimerControls";
import { CircularProgress } from "./timer/CircularProgress";
import { calculateProgress } from "./utils/timerUtils";

interface TimerProps {
  duration: number;
  isExerciseRest?: boolean;
  onComplete: () => void;
  onSkip: () => void;
  autoStart?: boolean;
}

export const Timer = ({
  duration,
  isExerciseRest = false,
  onComplete,
  onSkip,
  autoStart = true,
}: TimerProps) => {
  const [time, setTime] = useState(duration);
  const [isRunning, setIsRunning] = useState(autoStart);
  const progress = calculateProgress(time, duration);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRunning && time > 0) {
      interval = setInterval(() => {
        setTime((prevTime) => {
          if (prevTime <= 1) {
            setIsRunning(false);
            onComplete();
            toast({
              title: "Descanso completado",
              description: `Tiempo de descanso ${isExerciseRest ? 'entre ejercicios' : 'entre series'} completado`,
            });
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, time, onComplete, isExerciseRest]);

  const handleReset = () => {
    setIsRunning(false);
    setTime(duration);
  };

  const handleSkip = () => {
    setIsRunning(false);
    setTime(0);
    onSkip();
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-black via-black/98 to-black/95 flex flex-col items-center justify-center p-6 z-50">
      {/* Decorative background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-sm space-y-8 relative z-10">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-foreground">
            {isExerciseRest ? 'Descanso entre ejercicios' : 'Descanso entre series'}
          </h2>
          <p className="text-muted-foreground text-sm">
            Prepárate para la siguiente {isExerciseRest ? 'serie' : 'repetición'}
          </p>
        </div>

        <div className="relative aspect-square flex items-center justify-center">
          <CircularProgress progress={progress} />
          <TimerDisplay time={time} />
        </div>

        <TimerControls
          isRunning={isRunning}
          onPlayPause={() => setIsRunning(!isRunning)}
          onReset={handleReset}
          onSkip={handleSkip}
        />
      </div>
    </div>
  );
};
